/**
 * Data Validation and Normalization Utilities
 * Ensures all displayed data is consistent, realistic, and properly formatted.
 */

// ============= ODDS VALIDATION =============

export interface ValidatedOdds {
  home_win?: string;
  draw?: string;
  away_win?: string;
  over_2_5?: string;
  under_2_5?: string;
  btts_yes?: string;
  btts_no?: string;
}

const MIN_VALID_ODDS = 1.01;
const MAX_VALID_ODDS = 100.0;

/**
 * Validates a single odds value.
 * Returns null if the odds are invalid (< 1.01, > 100, NaN, empty, etc.)
 */
export function validateOddsValue(value: string | number | undefined | null): string | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue) || !isFinite(numValue)) {
    return null;
  }

  if (numValue < MIN_VALID_ODDS || numValue > MAX_VALID_ODDS) {
    return null;
  }

  // Return formatted to 2 decimal places
  return numValue.toFixed(2);
}

/**
 * Validates and normalizes an entire odds object.
 * Removes invalid odds and formats valid ones.
 */
export function validateOdds(odds: Record<string, any> | undefined | null): ValidatedOdds | null {
  if (!odds || typeof odds !== 'object') {
    return null;
  }

  const validated: ValidatedOdds = {};
  let hasValidOdds = false;

  const oddsFields = ['home_win', 'draw', 'away_win', 'over_2_5', 'under_2_5', 'btts_yes', 'btts_no'] as const;
  
  for (const field of oddsFields) {
    const validValue = validateOddsValue(odds[field]);
    if (validValue) {
      validated[field] = validValue;
      hasValidOdds = true;
    }
  }

  return hasValidOdds ? validated : null;
}

/**
 * Checks if odds object has any valid 1X2 odds (Match Winner market)
 */
export function hasValidMatchWinnerOdds(odds: ValidatedOdds | null): boolean {
  if (!odds) return false;
  return !!(odds.home_win || odds.draw || odds.away_win);
}

/**
 * Checks if odds object has valid goals market odds
 */
export function hasValidGoalsOdds(odds: ValidatedOdds | null): boolean {
  if (!odds) return false;
  return !!(odds.over_2_5 || odds.under_2_5);
}

/**
 * Checks if odds object has valid BTTS odds
 */
export function hasValidBTTSOdds(odds: ValidatedOdds | null): boolean {
  if (!odds) return false;
  return !!(odds.btts_yes || odds.btts_no);
}

// ============= MATCH DATA VALIDATION =============

export interface MatchMinuteValidation {
  isValid: boolean;
  displayMinute: string;
  rawMinute: number | null;
}

const MIN_MATCH_MINUTE = 0;
const MAX_MATCH_MINUTE = 130; // Accounts for extra time + injury time

/**
 * Validates and formats match minute for display.
 * Returns a safe display string and validation status.
 */
export function validateMatchMinute(minute: number | string | undefined | null): MatchMinuteValidation {
  if (minute === undefined || minute === null || minute === '') {
    return { isValid: false, displayMinute: '', rawMinute: null };
  }

  const numMinute = typeof minute === 'string' ? parseInt(minute, 10) : minute;

  if (isNaN(numMinute)) {
    return { isValid: false, displayMinute: '', rawMinute: null };
  }

  // Clamp to valid range
  const clampedMinute = Math.max(MIN_MATCH_MINUTE, Math.min(MAX_MATCH_MINUTE, numMinute));
  
  // Check if original was out of bounds
  const isValid = numMinute >= MIN_MATCH_MINUTE && numMinute <= MAX_MATCH_MINUTE;

  return {
    isValid,
    displayMinute: `${clampedMinute}'`,
    rawMinute: clampedMinute,
  };
}

/**
 * Validates match score values
 */
export function validateScore(score: number | null | undefined): number | null {
  if (score === undefined || score === null) {
    return null;
  }

  const numScore = typeof score === 'string' ? parseInt(score, 10) : score;
  
  if (isNaN(numScore) || numScore < 0 || numScore > 50) {
    return null;
  }

  return numScore;
}

// ============= STATISTICS VALIDATION =============

/**
 * Formats a percentage value consistently (0-100 range, no decimals)
 */
export function formatPercentage(value: number | string | undefined | null): string | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return null;
  }

  // Clamp to 0-100 range
  const clamped = Math.max(0, Math.min(100, numValue));
  return `${Math.round(clamped)}%`;
}

/**
 * Formats a decimal statistic (xG, shots avg, etc.) to consistent precision
 */
export function formatDecimalStat(
  value: number | string | undefined | null, 
  decimals: number = 2
): string | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue) || !isFinite(numValue)) {
    return null;
  }

  return numValue.toFixed(decimals);
}

/**
 * Formats an integer statistic (goals, shots, etc.)
 */
export function formatIntegerStat(value: number | string | undefined | null): string | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
  
  if (isNaN(numValue)) {
    return null;
  }

  return String(Math.round(numValue));
}

// ============= TEAM & LEAGUE VALIDATION =============

/**
 * Validates a team name is not empty or placeholder
 */
export function validateTeamName(name: string | undefined | null): string | null {
  if (!name || typeof name !== 'string') {
    return null;
  }

  const trimmed = name.trim();
  
  if (trimmed.length === 0 || trimmed.toLowerCase() === 'tbd' || trimmed === '-') {
    return null;
  }

  return trimmed;
}

/**
 * Validates a logo URL is valid and accessible
 */
export function validateLogoUrl(url: string | undefined | null): string | null {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const trimmed = url.trim();
  
  // Must be a valid URL
  try {
    new URL(trimmed);
  } catch {
    return null;
  }

  // Must be HTTPS
  if (!trimmed.startsWith('https://')) {
    return null;
  }

  return trimmed;
}

// ============= CONFIDENCE & PROBABILITY =============

/**
 * Validates confidence score is within bounds (1-100)
 */
export function validateConfidenceScore(score: number | undefined | null): number | null {
  if (score === undefined || score === null) {
    return null;
  }

  const numScore = typeof score === 'string' ? parseFloat(score) : score;
  
  if (isNaN(numScore)) {
    return null;
  }

  // Clamp to valid range
  return Math.max(1, Math.min(100, Math.round(numScore)));
}

/**
 * Validates risk score is within bounds (1-5)
 */
export function validateRiskScore(score: number | undefined | null): number | null {
  if (score === undefined || score === null) {
    return null;
  }

  const numScore = typeof score === 'string' ? parseFloat(score) : score;
  
  if (isNaN(numScore)) {
    return null;
  }

  // Clamp to valid range
  return Math.max(1, Math.min(5, Math.round(numScore)));
}

// ============= FORM VALIDATION =============

/**
 * Validates team form string (should be W, D, L characters only)
 */
export function validateTeamForm(form: string | undefined | null): string | null {
  if (!form || typeof form !== 'string') {
    return null;
  }

  const trimmed = form.trim().toUpperCase();
  
  // Only allow W, D, L characters
  const validChars = /^[WDL]+$/;
  if (!validChars.test(trimmed)) {
    return null;
  }

  // Limit to last 5 games
  return trimmed.slice(0, 5);
}

// ============= TIMESTAMP VALIDATION =============

/**
 * Validates a kickoff timestamp is valid and not too far in the past/future
 */
export function validateKickoffTime(time: string | Date | undefined | null): Date | null {
  if (!time) {
    return null;
  }

  const date = time instanceof Date ? time : new Date(time);
  
  if (isNaN(date.getTime())) {
    return null;
  }

  // Check if date is reasonable (within 1 year range)
  const now = new Date();
  const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
  const oneYearAhead = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);

  if (date < oneYearAgo || date > oneYearAhead) {
    return null;
  }

  return date;
}

// ============= COMPOSITE VALIDATION =============

export interface DataQualityReport {
  hasValidOdds: boolean;
  hasValidTeams: boolean;
  hasValidTime: boolean;
  hasValidStats: boolean;
  overallQuality: 'high' | 'medium' | 'low' | 'incomplete';
  warnings: string[];
}

/**
 * Generates a data quality report for a match
 */
export function assessMatchDataQuality(match: {
  home_team?: string | null;
  away_team?: string | null;
  kickoff_time?: string | Date | null;
  statistics?: { odds?: Record<string, any>; home?: any; away?: any } | null;
}): DataQualityReport {
  const warnings: string[] = [];

  const hasValidTeams = !!(validateTeamName(match.home_team) && validateTeamName(match.away_team));
  if (!hasValidTeams) warnings.push('Missing or invalid team names');

  const hasValidTime = !!validateKickoffTime(match.kickoff_time);
  if (!hasValidTime) warnings.push('Invalid kickoff time');

  const validatedOdds = validateOdds(match.statistics?.odds);
  const hasValidOdds = hasValidMatchWinnerOdds(validatedOdds);
  if (!hasValidOdds) warnings.push('Odds data unavailable');

  const hasValidStats = !!(match.statistics?.home || match.statistics?.away);
  if (!hasValidStats) warnings.push('Team statistics unavailable');

  let overallQuality: 'high' | 'medium' | 'low' | 'incomplete';
  if (hasValidTeams && hasValidTime && hasValidOdds && hasValidStats) {
    overallQuality = 'high';
  } else if (hasValidTeams && hasValidTime) {
    overallQuality = hasValidOdds || hasValidStats ? 'medium' : 'low';
  } else {
    overallQuality = 'incomplete';
  }

  return {
    hasValidOdds,
    hasValidTeams,
    hasValidTime,
    hasValidStats,
    overallQuality,
    warnings,
  };
}
